import { afterNextRender, DestroyRef, Directive, ElementRef, NgZone, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

type ThresholdType = 'pixel' | 'percent';

type Config = {
    threshold: number;
    type?: ThresholdType;
};

export type HoverState = {
    hover: boolean;
    leftThreshold: boolean;
    rightThreshold: boolean;
    topThreshold: boolean;
    bottomThreshold: boolean;
};

@Directive({ selector: '[webSingleHover]' })
export class SingleHoverDirective {
    private readonly elementRef = inject(ElementRef);
    private readonly ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    readonly webSingleHover = input<Config | ''>();
    readonly webSingleHoverDebug = input(false);
    readonly hovered = output<HoverState>();

    public state: HoverState = {
        hover: false,
        leftThreshold: false,
        rightThreshold: false,
        topThreshold: false,
        bottomThreshold: false,
    };

    private static readonly DEBUG_STATE: HoverState = {
        hover: true,
        leftThreshold: true,
        rightThreshold: true,
        topThreshold: true,
        bottomThreshold: true,
    };

    constructor() {
        afterNextRender(() => {
            if (this.webSingleHoverDebug()) {
                this.state = { ...SingleHoverDirective.DEBUG_STATE };
                this.hovered.emit({ ...this.state });
                return;
            }

            this.ngZone.runOutsideAngular(() => {
                const el = this.elementRef.nativeElement;

                fromEvent(el, 'mouseenter')
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => this.onMouseEnter());

                fromEvent(el, 'mouseleave')
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => this.onMouseLeave());

                if (this.webSingleHover()) {
                    fromEvent<MouseEvent>(el, 'mousemove')
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe((event) => this.onMouseMove(event));
                }
            });
        });
    }

    private onMouseEnter(): void {
        if (this.state.hover) {
            return;
        }
        this.state = { ...this.state, hover: true };
        this.ngZone.run(() => this.hovered.emit(this.state));
    }

    private onMouseLeave(): void {
        this.state = {
            hover: false,
            leftThreshold: false,
            rightThreshold: false,
            topThreshold: false,
            bottomThreshold: false,
        };
        this.ngZone.run(() => this.hovered.emit(this.state));
    }

    private onMouseMove(event: MouseEvent): void {
        const config = this.webSingleHover();
        if (!config) {
            return;
        }

        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        const threshold = config.threshold;
        const thresholdType = config.type || 'pixel';

        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        const thresholdWidth = thresholdType === 'pixel' ? threshold : (threshold * rect.width) / 100;
        const thresholdHeight = thresholdType === 'pixel' ? threshold : (threshold * rect.height) / 100;

        const leftThreshold = x < thresholdWidth;
        const rightThreshold = x > rect.width - thresholdWidth;
        const topThreshold = y < thresholdHeight;
        const bottomThreshold = y > rect.height - thresholdHeight;

        if (
            leftThreshold !== this.state.leftThreshold ||
            rightThreshold !== this.state.rightThreshold ||
            topThreshold !== this.state.topThreshold ||
            bottomThreshold !== this.state.bottomThreshold
        ) {
            this.state = { hover: true, leftThreshold, rightThreshold, topThreshold, bottomThreshold };
            this.ngZone.run(() => this.hovered.emit(this.state));
        }
    }
}
